import type { TurnstileInstance } from '@marsidev/react-turnstile';
import { Turnstile } from '@marsidev/react-turnstile';
import { useRef } from 'react';

import { CAPTCHA_PROVIDER } from '@shared/components/captcha/constants';
import { getTurnstileSitekey, googleCaptchaSitekeyV3 } from '@shared/configs/configs/config';

import useCaptchaProvider from '../useCaptchaProvider';

const useInvisibleCaptcha = () => {
  const turnstileRef = useRef<TurnstileInstance>(null);
  const pendingTokenRef = useRef<null | { reject: (error: Error) => void; resolve: (token: string) => void }>(null);
  const { captchaProvider } = useCaptchaProvider();

  const getToken = async (): Promise<string | undefined> => {
    if (captchaProvider === CAPTCHA_PROVIDER.turnstile) {
      if (!turnstileRef.current) {
        return Promise.reject(new Error('Turnstile widget not ready'));
      }

      return new Promise<string>((resolve, reject) => {
        pendingTokenRef.current = { resolve, reject };
        turnstileRef.current?.reset();
        turnstileRef.current?.execute();
      });
    }

    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(googleCaptchaSitekeyV3(), { action: 'submit' }).then(resolve).catch(reject);
      });
    });
  };

  const turnstileWidget = captchaProvider === CAPTCHA_PROVIDER.turnstile && (
    <Turnstile
      onError={(code) => pendingTokenRef.current?.reject(new Error(`Turnstile error ${code}`))}
      onExpire={() => pendingTokenRef.current?.reject(new Error('Turnstile token expired'))}
      onSuccess={(token) => pendingTokenRef.current?.resolve(token)}
      options={{
        execution: 'execute',
        retry: 'never',
        size: 'invisible',
      }}
      ref={turnstileRef}
      siteKey={getTurnstileSitekey()}
    />
  );

  return { getToken, turnstileWidget, captchaProvider };
};
export default useInvisibleCaptcha;
